import { Badge, Group, Paper, Stack, Switch, Text, Tooltip } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAdminQuery } from "../../api/admin";
import { api } from "../../api/client";
import type { ModuleId } from "../../api/types";

interface ModuleInfo {
  id: ModuleId;
  enabled: boolean;
  hardDisabled: boolean;
  soft: boolean;
}

/** Administrator's switches for the optional modules; .env-disabled ones are shown locked. */
export function ModulesPage() {
  const { t } = useTranslation();
  const list = useAdminQuery<{ modules: ModuleInfo[] }>("modules", "/api/admin/modules");
  const queryClient = useQueryClient();
  // The PUT answers with the whole list: it replaces the cached one instead of a refetch, so the
  // other rows do not blink; only the row being switched is locked meanwhile.
  const toggle = useMutation({
    mutationFn: (vars: { id: ModuleId; enabled: boolean }) =>
      api<{ modules: ModuleInfo[] }>(`/api/admin/modules/${vars.id}`, {
        method: "PUT",
        body: { enabled: vars.enabled },
      }),
    // Optimistic: the switch flips at once and rolls back if the server refuses.
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: ["admin", "modules"] });
      const previous = queryClient.getQueryData<{ modules: ModuleInfo[] }>(["admin", "modules"]);
      if (previous)
        queryClient.setQueryData(["admin", "modules"], {
          modules: previous.modules.map((m) =>
            m.id === vars.id ? { ...m, enabled: vars.enabled, soft: vars.enabled } : m,
          ),
        });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(["admin", "modules"], context.previous);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["admin", "modules"], data);
      void queryClient.invalidateQueries({ queryKey: ["lookups"] });
    },
  });
  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        {t("modules.hint")}
      </Text>
      <Stack gap="xs">
        {(list.data?.modules ?? []).map((m) => (
          <Paper key={m.id} withBorder p="sm">
            <Group justify="space-between" wrap="nowrap" align="flex-start">
              <Stack gap={2}>
                <Group gap="xs">
                  <Text fw={600}>{t(`modules.names.${m.id}`)}</Text>
                  <Badge size="xs" variant="light" color={m.enabled ? "green" : "gray"}>
                    {t(m.enabled ? "modules.on" : "modules.off")}
                  </Badge>
                  {m.hardDisabled && (
                    <Tooltip label={t("modules.locked")} multiline w={320}>
                      <Badge size="xs" variant="outline" color="orange">
                        .env
                      </Badge>
                    </Tooltip>
                  )}
                </Group>
                <Text size="xs" c="dimmed">
                  {t(`modules.descriptions.${m.id}`)}
                </Text>
              </Stack>
              <Switch
                checked={m.enabled}
                disabled={m.hardDisabled}
                styles={{ track: { cursor: m.hardDisabled ? "not-allowed" : "pointer" } }}
                aria-label={t(`modules.names.${m.id}`)}
                onChange={(e) =>
                  toggle
                    .mutateAsync({ id: m.id, enabled: e.currentTarget.checked })
                    .then(() => notifications.show({ message: t("modules.saved"), color: "green" }))
                    .catch((err: Error) =>
                      notifications.show({ message: err.message, color: "red" }),
                    )
                }
              />
            </Group>
          </Paper>
        ))}
      </Stack>
    </Stack>
  );
}
